import type { Home } from '@lmc/ts-common'
import {
  Button,
  ConfirmationModal,
  Descriptions,
  EmptyState,
  FormCard,
  NoDataIcon,
  PageHeader,
  Trash,
  theme,
  Warning
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import Actions from 'actions'
import { Form as AntdForm, Divider, Tag } from 'antd'
import { getNominative, showToast } from 'common/utils'
import { useCanEditServiceUserData } from 'components/AccessControl'
import GenericConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { RiskAssessmentsSection } from 'components/carePlans/RiskAssessmentsSection'
import type { ConsentFormData } from 'components/consent'
import ConsentSection from 'components/consent/ConsentSection'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useContingencyPlan from 'hooks/useContingencyPlan'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import Operations from 'operations'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  ButtonsWrapper,
  ProfileContent,
  ProfileWrapper
} from '../../styledComponents'
import { isPlanOverdue, scrollToTop } from '../../utils/utils'
import ContingencyPlanForm from './components/ContingencyPlanForm'
import {
  getContingencyPlanAdvancedStatementsFields,
  getContingencyPlanOtherFields,
  getContingencyPlanScenarioFields
} from './getContingencyPlanFields'
import type { ContingencyPlanExtended, LinkedRiskAssessment } from './types'

const getConsentFormIds = (plan?: ContingencyPlanExtended): string[] => {
  const consentForms = plan?.consentForms || []
  return consentForms.map((form) => (typeof form === 'string' ? form : form.id))
}

const getConsentForms = (plan?: ContingencyPlanExtended): ConsentFormData[] =>
  (plan?.consentForms || []).filter(
    (form): form is ConsentFormData => typeof form !== 'string'
  )

const getRiskAssessmentIds = (plan?: ContingencyPlanExtended): string[] => {
  const riskAssessments = plan?.riskAssessments || []
  return riskAssessments.map((riskAssessment) =>
    typeof riskAssessment === 'string' ? riskAssessment : riskAssessment.id
  )
}

const getRiskAssessments = (plan?: ContingencyPlanExtended) =>
  (plan?.riskAssessments || []).filter(
    (riskAssessment): riskAssessment is Exclude<LinkedRiskAssessment, string> =>
      typeof riskAssessment !== 'string'
  )

const RISK_LINK_FORM_ID = 'editContingencyPlan'
const RISK_ASSESSMENT_MODAL_ID = 'createRiskAssessment'

const ViewEditContingencyPlan = () => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const featureFlags = useFeatureFlags({
    flagNames: [
      'informed_consent',
      'consent_future_plan',
      'risk_assessment_future_plan'
    ]
  }).data
  const isInformedConsentEnabled =
    featureFlags.informed_consent?.enabled || false
  const isConsentFuturePlanEnabled =
    featureFlags.consent_future_plan?.enabled || false
  const isRiskAssessmentFuturePlanEnabled =
    featureFlags.risk_assessment_future_plan?.enabled || false
  const { t: generalTranslations } = useTranslation(NAMESPACE.GENERAL)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const canEditServiceUserData = useCanEditServiceUserData()
  const [isDirty, setIsDirty] = useState(false)
  const [isReviewing, setIsReviewing] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [consentFormIds, setConsentFormIds] = useState<string[]>([])
  const { contingencyPlanID, id } = useParams()
  const dispatch = useDispatch()
  const {
    contingencyPlan,
    deleteContingencyPlan,
    isDeleteLoading,
    isContingencyPlanLoading
  } = useContingencyPlan({
    contingencyPlanID,
    onDeleteSuccess: () => {
      showToast({
        title: t(supConstants.FUTURE_PLAN_DELETED)
      })
      navigate(`../`, { relative: 'path' })
    }
  })
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const carers = useSelector((state: any) => state.data.carers)
  const riskAssessments = useSelector(
    (state: any) => state.data['risk-assessments']
  )
  const linkedRiskAssessmentIds = useSelector(
    (state: {
      forms?: Record<string, { fields?: { riskAssessments?: string[] } }>
    }) => state.forms?.[RISK_LINK_FORM_ID]?.fields?.riskAssessments || []
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const planName = contingencyPlan?.planName

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!contingencyPlan) return

    // Consent form state must initialize regardless of risk-assessment linking flag.
    setConsentFormIds(getConsentFormIds(contingencyPlan))

    if (isRiskAssessmentFuturePlanEnabled) {
      dispatch(
        Actions.addForm({
          formId: RISK_LINK_FORM_ID,
          initial: {
            riskAssessments: getRiskAssessmentIds(contingencyPlan),
            reviewedAssessments: []
          }
        })
      )
    }
  }, [contingencyPlan, dispatch, isRiskAssessmentFuturePlanEnabled])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!id || !isRiskAssessmentFuturePlanEnabled) return
    dispatch(
      Operations.loadList('risk-assessments', {
        resident: id,
        mostRecent: true
      })
    )
  }, [id, dispatch, isRiskAssessmentFuturePlanEnabled])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    return () => {
      if (isRiskAssessmentFuturePlanEnabled) {
        dispatch(Actions.removeForm(RISK_LINK_FORM_ID))
      }
    }
  }, [dispatch, isRiskAssessmentFuturePlanEnabled])

  const handleSetRiskAssessmentField = ({
    key,
    value
  }: {
    key: string
    value: string[]
  }) => {
    setIsDirty(true)
    dispatch(
      Actions.setFormField({
        key,
        value: value as any,
        formId: RISK_LINK_FORM_ID
      })
    )
  }

  const openRiskAssessmentModal = (completedRiskAssessment?: unknown) => {
    dispatch(
      Actions.openModal({
        modalId: RISK_ASSESSMENT_MODAL_ID,
        data: {
          completedRiskAssessment,
          parentFormId: RISK_LINK_FORM_ID,
          linkedContingencyPlanId: contingencyPlanID
        }
      })
    )
  }

  if (isContingencyPlanLoading) {
    return <Spinner />
  }

  if (!contingencyPlan) {
    return <EmptyState title={t(supConstants.NO_PLAN)} icon={<NoDataIcon />} />
  }

  return (
    <ProfileWrapper ref={topRef}>
      <PageHeader
        backButtonHref="../"
        sticky
        childrenTitle={
          <TitleContainer>
            <StyledTitle level={1}>
              {planName || t(supConstants.FUTURE_PLAN)}
            </StyledTitle>
            {isPlanOverdue(contingencyPlan.nextReviewDate) ? (
              <Tag color="red">{t(supConstants.OVERDUE)}</Tag>
            ) : null}
          </TitleContainer>
        }
      >
        {!isReviewing ? (
          <Button
            variant="primary"
            onClick={() => setIsReviewing(true)}
            disabledByPermission={!canEditServiceUserData}
          >
            {t(supConstants.REVIEW)}
          </Button>
        ) : (
          <ButtonsWrapper>
            <ConfirmationModal
              localisation={{
                confirmText: generalTranslations(generalConstants.DELETE),
                cancelText: generalTranslations(generalConstants.CANCEL)
              }}
              modalContent={
                <ConfirmationModalContent>
                  <ConfirmationModalTitleContainer>
                    <Warning color={theme.color.status.warning} size={24} />
                    <Heading level={4}>
                      {t(supConstants.DELETE_PLAN_CONFIRMATION)}
                    </Heading>
                  </ConfirmationModalTitleContainer>
                  <Divider />
                  <Text> {t(supConstants.DELETE_PLAN_INFO)}</Text>
                  <Divider />
                </ConfirmationModalContent>
              }
            >
              <Button
                variant="danger"
                isLoading={isDeleteLoading}
                onClick={() => deleteContingencyPlan()}
                disabledByPermission={!canEditServiceUserData}
              >
                <Trash />
                {t(supConstants.DELETE)}
              </Button>
            </ConfirmationModal>
            <GenericConfirmationModal shouldPrompt={isDirty}>
              <Button
                onClick={() => {
                  setIsDirty(false)
                  setConsentFormIds(getConsentFormIds(contingencyPlan))
                  if (isRiskAssessmentFuturePlanEnabled) {
                    dispatch(
                      Actions.addForm({
                        formId: RISK_LINK_FORM_ID,
                        initial: {
                          riskAssessments:
                            getRiskAssessmentIds(contingencyPlan),
                          reviewedAssessments: []
                        }
                      })
                    )
                  }
                  setIsReviewing(false)
                }}
              >
                {t(supConstants.CANCEL)}
              </Button>
            </GenericConfirmationModal>
            <Button
              variant="primary"
              onClick={form.submit}
              type="submit"
              isLoading={isUpdateLoading}
              disabledByPermission={!canEditServiceUserData}
            >
              {t(supConstants.SAVE)}
            </Button>
          </ButtonsWrapper>
        )}
      </PageHeader>
      <ProfileContent>
        {isReviewing ? (
          <>
            <NavigationModal canNavigate={!isDirty} />
            <ContingencyPlanForm
              disabled={!canEditServiceUserData}
              form={form}
              plan={contingencyPlan}
              consentFormIds={consentFormIds}
              riskAssessmentIds={linkedRiskAssessmentIds}
              isConsentFuturePlanEnabled={isConsentFuturePlanEnabled}
              isRiskAssessmentFuturePlanEnabled={
                isRiskAssessmentFuturePlanEnabled
              }
              setIsUpdateLoading={setIsUpdateLoading}
              setIsDirty={setIsDirty}
            >
              {isRiskAssessmentFuturePlanEnabled && (
                <RiskAssessmentsSection
                  readOnly={false}
                  compactSpacing
                  carers={carers}
                  fields={{
                    id: contingencyPlan.id,
                    editedOn: contingencyPlan.editedOn,
                    riskAssessments: linkedRiskAssessmentIds,
                    reviewedAssessments: [],
                    populatedRiskAssessments:
                      getRiskAssessments(contingencyPlan)
                  }}
                  riskAssessments={riskAssessments}
                  router={{ params: { id }, navigate }}
                  setFormField={handleSetRiskAssessmentField}
                  onOpenModal={openRiskAssessmentModal}
                  carePlanNominative="Future Plan"
                />
              )}
              {!!id &&
                isInformedConsentEnabled &&
                isConsentFuturePlanEnabled && (
                  <ConsentSection
                    residentId={id}
                    value={consentFormIds}
                    onChange={(selectedIds) => {
                      setConsentFormIds(selectedIds)
                      setIsDirty(true)
                    }}
                  />
                )}
            </ContingencyPlanForm>
          </>
        ) : (
          <>
            {contingencyPlan?.scenarios?.map((scenario, i) => (
              <FormCard
                key={scenario._id}
                title={`${t(supConstants.SCENARIO)} ${i + 1}`}
              >
                <Descriptions
                  labelStyle={labelStyle}
                  items={getContingencyPlanScenarioFields(
                    scenario,
                    residentNominative,
                    t
                  )}
                  bordered
                  column={1}
                />
              </FormCard>
            ))}
            {contingencyPlan?.advancedStatements?.map(
              (advancedStatement, i) => (
                <FormCard
                  key={advancedStatement._id}
                  title={`${t(supConstants.ADVANCED_STATEMENT)} ${i + 1}`}
                >
                  <Descriptions
                    labelStyle={labelStyle}
                    items={getContingencyPlanAdvancedStatementsFields(
                      advancedStatement,
                      t
                    )}
                    bordered
                    column={1}
                  />
                </FormCard>
              )
            )}
            {isRiskAssessmentFuturePlanEnabled && (
              <RiskAssessmentsSection
                readOnly
                compactSpacing
                carers={carers}
                fields={{
                  id: contingencyPlan.id,
                  editedOn: contingencyPlan.editedOn,
                  riskAssessments: getRiskAssessmentIds(contingencyPlan),
                  reviewedAssessments: [],
                  populatedRiskAssessments: getRiskAssessments(contingencyPlan)
                }}
                riskAssessments={riskAssessments}
                router={{ params: { id }, navigate }}
                carePlanNominative="Future Plan"
              />
            )}
            {!!id && isInformedConsentEnabled && isConsentFuturePlanEnabled && (
              <ConsentSection
                residentId={id}
                consentForms={getConsentForms(contingencyPlan)}
                disabled
              />
            )}
            <FormCard title={t(supConstants.OTHER_DETAILS)}>
              <Descriptions
                labelStyle={labelStyle}
                items={getContingencyPlanOtherFields(
                  contingencyPlan,
                  residentNominative,
                  t
                )}
                bordered
                column={1}
              />
            </FormCard>
          </>
        )}
      </ProfileContent>
    </ProfileWrapper>
  )
}

const labelStyle = { width: 272 }

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledTitle = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const ConfirmationModalContent = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.color.neutral.color3};
`

const ConfirmationModalTitleContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

export default ViewEditContingencyPlan
